import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import Loading from '../../Shared/Loading';
import { useNotification } from '../../../context/NotificationContext';
import { City, EmploymentHistory, State } from '../../../Models';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { GetAllCompsByName } from '../../../api/masterdata.api';
import useApiToken from '../../../hooks/useApiToken';
interface Company {
  id: string;
  name: string;
  url: string;
  city: City;
  state: State;
  location: string;
}

export default function NewExperienceModal({
  size,
  isOpen,
  onCancel,
  isModalConfirm,
  experience,
  cities,
  states
}: {
  cities: City[],
  states: State[],
  onCancel: Function,
  isOpen: boolean,
  isModalConfirm: Function,
  size: string,
  experience: EmploymentHistory & { id: string } | null | undefined
}) {
  const token = useApiToken();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const cancelButtonRef = useRef(null);
  const { error: notifyError } = useNotification();

  // Company search states
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Form state
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [companyUrl, setCompanyUrl] = useState('');
  const [title, setTitle] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [description, setDescription] = useState('');
  const [availableCities, setAvailableCities] = useState<City[]>([]);
  const [isPresent, setIsPresent] = useState<boolean>(false);
  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  // Initialize form data
  useEffect(() => {
    if (experience) {
      setCompanySearch(experience.company || '');
      setCompanyUrl(experience.url || '');
      setTitle(experience.title || '');
      setDescription(experience.description || '');

      const [from, to] = (experience.period || '').split(' - ');
      if(from==="MM/YY"){
        setFromYear('');
      }
      else{
        setFromYear(from || '');
      }
      setToYear(to || '');
      if (to === "Present") {
        setIsPresent(true);
      }
      else {
        setIsPresent(false);
      }

      if (experience.city) {
        const cityObj = cities.find(c => c.name === experience.city.split(',')[0]);
        if (cityObj?.state) {
          setSelectedState(cityObj.state.id);
          setSelectedCity(cityObj.id);
          setAvailableCities(cities.filter(c => c.state?.id === cityObj.state?.id));
        }
      }
    }
  }, [experience, cities]);

  const handleCompanySearch = async (searchTerm: string) => {
    if (searchTerm === '') {
      setSelectedCompany(null);
      setSelectedState('');
      setSelectedCity('');
      setCompanyUrl('');
    }
    setCompanySearch(searchTerm);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    if (searchTerm.length < 3) {
      setCompanies([]);

      setShowCompanyDropdown(false);
      return;
    }

    setLoading(true);
    const timeout = setTimeout(async () => {
      if (!token) return;
      try {
        const results = await GetAllCompsByName(searchTerm, token);
        setCompanies(results);
        setShowCompanyDropdown(true);
      } catch (err) {
        console.error('Error searching companies:', err);
        notifyError('Error', 'Error searching companies');
      } finally {
        setLoading(false);
      }
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setCompanySearch(company.name);
    setSelectedState(company.state?.id || '');
    setSelectedCity(company.city?.id || '');
    setCompanyUrl(company.url || '');
    setShowCompanyDropdown(false);

    setAvailableCities(cities.filter(city => city.state?.id === company.state?.id));
  };

  const handleSubmit = () => {
    if (!companySearch || !selectedState || !selectedCity || !fromYear || !toYear || !title || !companyUrl) {
      notifyError('Error', 'Please fill in all required fields');
      return;
    }

    if (selectedCompany) {
      isModalConfirm(selectedCompany, `${fromYear} - ${toYear}`, experience?.id, "old");
    } else {
      const formData = {
        id: experience?.id,
        name: companySearch,
        stateId: selectedState,
        cityId: selectedCity,
        url: companyUrl,
        title: title,
        isConfirmed: true,
        isRejected: false,
        abbreviation: '',
        city: cities.find(c => c.id === selectedCity)?.name,
        state: states.find(c => c.id === selectedState)?.name,
        period: `${fromYear} - ${toYear}`,
        description,
      };
      isModalConfirm(formData, `${fromYear} - ${toYear}`, experience?.id, "new");

    }
    setOpen(false);
    setTimeout(() => {
      setSelectedCompany(null);
    }, 500)

  };
  const formatDate = (date: Date | null) => {
    if (!date) return "";
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString().slice(-2);
    return `${month}/${year}`;
  };
  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10 dark:bg-entntdarkblue"
        initialFocus={cancelButtonRef}
        onClose={() => {
          onCancel();
          setOpen(false);
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-400 bg-opacity-50 dark:bg-opacity-20 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full h-fit flex flex-col dark:bg-darkbg sm:max-w-lg">
                <div className="bg-gray-200 h-12 p-4 dark:bg-darkbglight flex flex-row items-center justify-between">
                  <h2 className="font-bold text-gray-600 text-sm text-left dark:text-gray-300">
                    Add Experience Details
                  </h2>
                  <span className='text-red-500 text-xs'>Company was not found</span>
                </div>

                <div className="p-6 space-y-4">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Job Title *
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                      placeholder="Enter your Job Title"
                    />
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      value={companySearch}
                      onChange={(e) => {
                        handleCompanySearch(e.target.value)

                      }}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                      placeholder="Search for company..."
                    />

                    {showCompanyDropdown && companies && companies.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-darkbglight border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                        {companies.map((company) => (
                          <div
                            key={company.id}
                            className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => handleCompanySelect(company)}
                          >
                            <div className="font-medium dark:text-gray-300">{company?.name}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {company?.city?.name}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {showCompanyDropdown && !selectedCompany && companies.length === 0 && <span className='text-red-500 text-xs'>Company not found. </span>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Company URL
                    </label>
                    <input
                      type="text"
                      value={companyUrl}
                      onChange={(e) => setCompanyUrl(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                      placeholder="eg. www.company.com"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                      placeholder="Enter job description"
                    />
                  </div>

                  {/* Period */}
                  <div className="grid grid-cols-2 gap-4">
                  <div className='flex flex-col '>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        From Year *
                      </label>
                      <DatePicker
                        selected={
                          fromYear
                            ? new Date(
                              2000 + Number(fromYear.split("/")[1]), 
                              Number(fromYear.split("/")[0]) - 1, 
                              1
                            )
                            : null
                        }
                        onChange={(date) => setFromYear(formatDate(date))}
                        showMonthYearPicker
                        dateFormat="MM/yy"
                        minDate={new Date(2000, 0, 1)}
                        className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                        placeholderText="MM/YY"
                      />
                    </div>

                    <div className='flex flex-col'>
                      <div className="flex flex-row items-center justify-between">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          To Year *
                        </label>
                        <div className="dark:text-gray-300 text-gray-700">
                          <label htmlFor="present-checkbox" className="text-xs">
                            Currently Working
                          </label>
                          <input
                            type="checkbox"
                            className="ml-4"
                            id="present-checkbox"
                            checked={isPresent}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setToYear("Present");
                                setIsPresent(true);
                              } else {
                                setIsPresent(false);
                                setToYear("");
                              }
                            }}
                          />
                        </div>
                      </div>

                      <DatePicker
                        selected={
                          toYear && (toYear !== "Present")
                            ? new Date(
                              2000 + Number(toYear.split("/")[1]), // Convert YY back to full year
                              Number(toYear.split("/")[0]) - 1, // Convert MM (zero-based index)
                              1
                            )
                            : null
                        }
                        onChange={(date) => setToYear(formatDate(date))}
                        showMonthYearPicker
                        dateFormat="MM/yy"
                        minDate={
                          fromYear
                            ? new Date(
                              2000 + Number(fromYear.split("/")[1]), // Convert YY back to full year
                              Number(fromYear.split("/")[0]) - 1, // Convert MM (zero-based index)
                              1
                            )
                            : new Date(2000, 0, 1)
                        }
                        disabled={isPresent}
                        className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                        placeholderText="MM/YY"
                      />
                    </div>
                  </div>

                  {/* State Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      State *
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setAvailableCities(cities.filter(city => city.state?.id === e.target.value));
                      }}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* City Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      City *
                    </label>
                    <select
                      value={selectedCity}
                      onChange={(e) => setSelectedCity(e.target.value)}
                      disabled={!selectedState}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-darkbglight dark:border-gray-600 dark:text-gray-300"
                    >
                      <option value="">Select City</option>
                      {availableCities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="bg-gray-50 px-4 py-3 sm:flex flex-row items-center justify-between sm:px-6 dark:bg-darkbglight">
                  <div>
                    {!selectedCompany && <span className='text-red-500 text-xs'>New Company will be created. </span>}
                  </div>
                  <div className='flex flex-row items-center gap-2'>
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                      onClick={handleSubmit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        onCancel();
                        setOpen(false);
                      }}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                  </div>
                </div>

              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}